/**
 * Reglas de aprobación / COMPLETION — espejo TS del motor Rust
 * (`packages/core-rust/src/completion`). El SERVIDOR es la autoridad (cierre,
 * `/api/cohorts/:id/my-completion`, `/completion-report`); este espejo cubre:
 *   • metadata auto-descriptiva de cada criterio (info-hints del editor);
 *   • el árbol de config (`CompletionConfig`) + builders (para el editor);
 *   • un evaluador PURO idéntico al de Rust (preview local + tests de paridad);
 *   • selectores del progreso del alumno (línea "Asistencia 80% ✓ · …");
 *   • los manifests `academy.completion.<key>` para el catálogo de features.
 *
 * Mantener EN SYNC con el Rust: cualquier criterio nuevo se añade en ambos.
 */

import type { FeatureManifest } from "../country/packs";

// ── Keys de criterios (== Rust `completion::criteria`) ─────────────────────────

export const COMPLETION_CRITERIA = [
  "attendance_pct",
  "challenges_passed",
  "final_project",
  "min_final_grade",
  "manual",
  // B6 · progresión por etapas. El desbloqueo de un «nivel» habla el MISMO
  // lenguaje que la aprobación: prerrequisito duro = `all(stage_completed)`,
  // salto de nivel = `any(stage_completed, placement_test)`, pago =
  // `entitlement`. Cero motores de reglas nuevos.
  "stage_completed",
  "placement_test",
  "entitlement",
  "cohort_active",
] as const;

export type CompletionCriterionKey = (typeof COMPLETION_CRITERIA)[number];

// ── Árbol de configuración (== Rust serde `CompletionNode`) ────────────────────

export type CompletionNode =
  | { type: "all"; children: CompletionNode[] }
  | { type: "any"; children: CompletionNode[] }
  | { type: "criterion"; key: string; params?: Record<string, unknown> };

export interface CompletionConfig {
  root: CompletionNode;
}

/** ¿El nodo tiene contenido evaluable? (`all`/`any` sin hijos = vacío). */
export function isMeaningful(node: CompletionNode): boolean {
  if (node.type === "criterion") {
    return true;
  }
  return node.children.some(isMeaningful);
}

export const criterionNode = (
  key: CompletionCriterionKey,
  params: Record<string, unknown> = {},
): CompletionNode => ({ type: "criterion", key, params });
export const allNode = (...children: CompletionNode[]): CompletionNode => ({
  type: "all",
  children,
});
export const anyNode = (...children: CompletionNode[]): CompletionNode => ({
  type: "any",
  children,
});

/** Puerta de nota heredada (fallback de una config vacía) — retro-compat exacta. */
export function legacyConfig(): CompletionConfig {
  return { root: allNode(criterionNode("min_final_grade")) };
}

/**
 * Plantilla RECOMENDADA (la que pre-rellena el editor y siembra el demo):
 * **nota ≥ aprobado  O  (asistencia ≥ 75 % Y proyecto final)**.
 */
export function recommendedDefaultConfig(): CompletionConfig {
  return {
    root: anyNode(
      criterionNode("min_final_grade"),
      allNode(criterionNode("attendance_pct", { min_pct: 75 }), criterionNode("final_project")),
    ),
  };
}

/** Type-guard estructural (sin type-assertions) del árbol de config. */
function isCompletionConfigShape(v: unknown): v is CompletionConfig {
  return (
    typeof v === "object" &&
    v !== null &&
    "root" in v &&
    typeof v.root === "object" &&
    v.root !== null
  );
}

/** Parsea el jsonb de la columna (`null`/`{}`/árbol vacío → `null`). */
export function parseCompletionConfig(raw: unknown): CompletionConfig | null {
  if (raw == null) {
    return null;
  }
  let value: unknown = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "{}") {
      return null;
    }
    try {
      value = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!isCompletionConfigShape(value)) {
    return null;
  }
  return isMeaningful(value.root) ? value : null;
}

// ── Metadata auto-descriptiva de cada criterio (info-hints del editor) ─────────

export interface CompletionParamSpec {
  key: string;
  /** Etiqueta corta es-VE (la UI puede localizar). */
  label: string;
  // `text` lo añade B6: los criterios de progresión referencian entidades por
  // id (un `stage_key`, un alcance de titularidad), no cantidades.
  type: "percent" | "integer" | "text";
  /** Valor por defecto sugerido. */
  default?: number;
}

export interface CompletionCriterionMeta {
  key: CompletionCriterionKey;
  /** Etiqueta corta para chips de progreso ("Asistencia", "Desafíos"…). */
  shortLabel: string;
  name: string;
  description: string;
  params: CompletionParamSpec[];
}

export const COMPLETION_CRITERIA_META: Record<CompletionCriterionKey, CompletionCriterionMeta> = {
  attendance_pct: {
    key: "attendance_pct",
    shortLabel: "Asistencia",
    name: "Asistencia mínima",
    description:
      "Exige un porcentaje mínimo de asistencia (sesiones presente o tarde sobre las registradas).",
    params: [
      {
        key: "min_pct",
        label: "Asistencia mínima (%)",
        type: "percent",
        default: 75,
      },
    ],
  },
  challenges_passed: {
    key: "challenges_passed",
    shortLabel: "Desafíos",
    name: "Desafíos aprobados",
    description:
      "Exige aprobar un número de desafíos (tareas del curso que no son el proyecto final). Sin número, exige todos.",
    params: [{ key: "count", label: "Nº de desafíos", type: "integer" }],
  },
  final_project: {
    key: "final_project",
    shortLabel: "Proyecto final",
    name: "Proyecto final aprobado",
    description:
      "Exige aprobar el proyecto final del curso (tarea de tipo proyecto calificada ≥ umbral).",
    params: [],
  },
  min_final_grade: {
    key: "min_final_grade",
    shortLabel: "Nota",
    name: "Nota mínima",
    description:
      "Exige que la nota final ponderada alcance un umbral. Sin umbral usa el del modelo de calificación (aprobado).",
    params: [{ key: "min_pct", label: "Nota mínima (%)", type: "percent" }],
  },
  manual: {
    key: "manual",
    shortLabel: "Instructor",
    name: "Marca del instructor",
    description: "El instructor marca manualmente al alumno como completo.",
    params: [],
  },
  stage_completed: {
    key: "stage_completed",
    shortLabel: "Nivel previo",
    name: "Nivel anterior completado",
    description:
      "Exige haber completado el nivel anterior de la ruta (o uno concreto). Dentro de un grupo «todas», es un prerrequisito duro; dentro de «alguna», una vía entre varias.",
    params: [{ key: "stage_key", label: "Nivel concreto (vacío = el anterior)", type: "text" }],
  },
  placement_test: {
    key: "placement_test",
    shortLabel: "Nivelación",
    name: "Prueba de nivelación aprobada",
    description:
      "Permite saltar al nivel sin cursar el anterior si se aprueba una prueba. Quién aprueba lo decide el servidor al corregir, no el dispositivo.",
    params: [{ key: "min_pct", label: "Nota mínima (%)", type: "percent", default: 70 }],
  },
  entitlement: {
    key: "entitlement",
    shortLabel: "Pago",
    name: "Acceso pagado",
    description:
      "Exige que el alumno tenga derecho de acceso vigente (matrícula al día). Lee la titularidad que ya calcula la academia; no consulta facturas.",
    params: [{ key: "scope", label: "Alcance (stage, track, cohort, course)", type: "text" }],
  },
  cohort_active: {
    key: "cohort_active",
    shortLabel: "Cohorte",
    name: "Cohorte en marcha",
    description: "Exige estar matriculado en una cohorte activa de este nivel.",
    params: [],
  },
};

/**
 * Criterios que solo tienen sentido dentro de una RUTA. El editor de reglas de
 * una cohorte no los ofrece (no hay etapa anterior que exigir en un curso
 * suelto) y el de una etapa sí. Espejo de `PROGRESSION_CRITERIA` en Rust.
 */
export const PROGRESSION_CRITERIA: readonly CompletionCriterionKey[] = [
  "stage_completed",
  "placement_test",
  "entitlement",
  "cohort_active",
];

export function completionCriterionMeta(key: string): CompletionCriterionMeta | undefined {
  return (COMPLETION_CRITERIA_META as Record<string, CompletionCriterionMeta>)[key];
}

/** Manifests auto-descriptivos de los criterios (`academy.completion.<key>`). */
export function completionCriterionManifests(): FeatureManifest[] {
  return COMPLETION_CRITERIA.map((key) => ({
    key: `academy.completion.${key}`,
    name: COMPLETION_CRITERIA_META[key].name,
    description: COMPLETION_CRITERIA_META[key].description,
    scope: "course" as const,
    status: "beta" as const,
    // Los criterios de progresión cuelgan de su vertical, no de las reglas de
    // aprobación: sin rutas no hay etapa anterior que exigir.
    requires: PROGRESSION_CRITERIA.includes(key)
      ? ["academy.progression"]
      : ["academy.completion_rules"],
  }));
}

// ── Evaluador PURO (espejo de Rust `evaluate_config`) ──────────────────────────

export interface EvalContext {
  attendanceRate: number | null;
  challengesTotal: number;
  challengesPassed: number;
  finalProject: boolean | null;
  finalGradeRaw: number;
  passingThreshold: number;
  manualComplete: boolean;
  // ── B6 · progresión (ampliación ADITIVA; todos opcionales para que ningún
  // llamador existente tenga que cambiar) ─────────────────────────────────────
  /** `stage_key` de las etapas ya completadas. */
  stagesCompleted?: readonly string[];
  /** `stage_key` de la etapa anterior a la evaluada. */
  previousStageKey?: string | null;
  /** `target_stage_key` de las pruebas de nivelación aprobadas y vigentes. */
  placementPassed?: readonly string[];
  /** Titularidades vigentes como `"<kind>:<id>"`. */
  entitlements?: readonly string[];
  /** Alcance que resuelve `"ref": "self"`. */
  selfEntitlementRef?: string | null;
  activeCohort?: boolean;
}

export interface CriterionOutcome {
  met: boolean;
  current: number;
  target: number;
  detail: string;
}

const round1 = (x: number): number => Math.round(x * 10) / 10;

function evalCriterion(
  criterionKey: string,
  params: Record<string, unknown>,
  ctx: EvalContext,
): CriterionOutcome {
  const num = (f: string): number | undefined => {
    const v = params[f];
    return typeof v === "number" ? v : undefined;
  };
  switch (criterionKey) {
    case "attendance_pct": {
      const target = num("min_pct") ?? 75;
      if (ctx.attendanceRate == null) {
        return {
          met: false,
          current: 0,
          target,
          detail: "Sin registro de asistencia",
        };
      }
      const current = round1(ctx.attendanceRate * 100);
      return {
        met: current + 1e-9 >= target,
        current,
        target,
        detail: `Asistencia ${current}% (mín. ${target}%)`,
      };
    }
    case "challenges_passed": {
      const target = num("count") ?? ctx.challengesTotal;
      const current = ctx.challengesPassed;
      return {
        met: current + 1e-9 >= target,
        current,
        target,
        detail: `${current}/${target} desafíos aprobados`,
      };
    }
    case "final_project": {
      if (ctx.finalProject === true) {
        return {
          met: true,
          current: 1,
          target: 1,
          detail: "Proyecto final aprobado",
        };
      }
      if (ctx.finalProject === false) {
        return {
          met: false,
          current: 0,
          target: 1,
          detail: "Proyecto final pendiente",
        };
      }
      return {
        met: false,
        current: 0,
        target: 1,
        detail: "El curso no tiene proyecto final",
      };
    }
    case "min_final_grade": {
      const threshold = num("min_pct") != null ? num("min_pct")! / 100 : ctx.passingThreshold;
      const current = round1(ctx.finalGradeRaw * 100);
      const target = round1(threshold * 100);
      return {
        met: Math.min(Math.max(ctx.finalGradeRaw, 0), 1) + Number.EPSILON >= threshold,
        current,
        target,
        detail: `Nota ${current}% (mín. ${target}%)`,
      };
    }
    case "manual": {
      return {
        met: ctx.manualComplete,
        current: ctx.manualComplete ? 1 : 0,
        target: 1,
        detail: ctx.manualComplete
          ? "Marcado como completo por el instructor"
          : "Pendiente de marca del instructor",
      };
    }
    // ── B6 · progresión por etapas ───────────────────────────────────────────
    case "stage_completed": {
      const wantsPrevious = params.previous === true;
      const targetKey = wantsPrevious
        ? ctx.previousStageKey
        : typeof params.stage_key === "string"
          ? params.stage_key
          : undefined;
      if (targetKey == null) {
        // Sin etapa anterior, la primera de la ruta está ABIERTA: un
        // prerrequisito imposible dejaría la ruta cerrada de origen.
        return wantsPrevious
          ? { met: true, current: 1, target: 1, detail: "Primera etapa de la ruta" }
          : { met: false, current: 0, target: 1, detail: "Etapa requerida sin identificar" };
      }
      const met = (ctx.stagesCompleted ?? []).includes(targetKey);
      return {
        met,
        current: met ? 1 : 0,
        target: 1,
        detail: met ? "Etapa previa completada" : "Te falta completar la etapa anterior",
      };
    }
    case "placement_test": {
      const target = num("min_pct") ?? 70;
      const key =
        typeof params.target_stage_key === "string"
          ? params.target_stage_key
          : (ctx.selfEntitlementRef?.replace(/^stage:/, "") ?? undefined);
      const met = key != null && (ctx.placementPassed ?? []).includes(key);
      return {
        met,
        current: met ? target : 0,
        target,
        detail: met
          ? "Prueba de nivelación aprobada"
          : `Prueba de nivelación pendiente (mín. ${target}%)`,
      };
    }
    case "entitlement": {
      const scope = typeof params.scope === "string" ? params.scope : "stage";
      const reference = typeof params.ref === "string" ? params.ref : "self";
      const wanted = reference === "self" ? ctx.selfEntitlementRef : `${scope}:${reference}`;
      const met = wanted != null && (ctx.entitlements ?? []).includes(wanted);
      return {
        met,
        current: met ? 1 : 0,
        target: 1,
        detail: met ? "Acceso pagado" : "Falta completar el pago de esta etapa",
      };
    }
    case "cohort_active": {
      const met = ctx.activeCohort === true;
      return {
        met,
        current: met ? 1 : 0,
        target: 1,
        detail: met
          ? "Matrícula activa en una cohorte en marcha"
          : "Sin cohorte activa para esta etapa",
      };
    }
    default: {
      return {
        met: false,
        current: 0,
        target: 0,
        detail: `criterio desconocido: ${criterionKey}`,
      };
    }
  }
}

export interface CriterionBreakdown {
  key: string;
  met: boolean;
  current: number;
  target: number;
  detail: string;
  params: Record<string, unknown>;
}

export interface CompletionStatus {
  passed: boolean;
  configured: boolean;
  criteria: CriterionBreakdown[];
}

function evalNode(node: CompletionNode, ctx: EvalContext, out: CriterionBreakdown[]): boolean {
  if (node.type === "criterion") {
    const o = evalCriterion(node.key, node.params ?? {}, ctx);
    out.push({ key: node.key, ...o, params: node.params ?? {} });
    return o.met;
  }
  if (node.type === "all") {
    let all = true;
    for (const c of node.children) {
      if (!evalNode(c, ctx, out)) {
        all = false;
      }
    }
    return all;
  }
  // any
  let any = false;
  for (const c of node.children) {
    if (evalNode(c, ctx, out)) {
      any = true;
    }
  }
  return node.children.length > 0 && any;
}

/** Evalúa la config contra un contexto (espejo puro del motor Rust). */
export function evaluateCompletion(config: CompletionConfig, ctx: EvalContext): CompletionStatus {
  const criteria: CriterionBreakdown[] = [];
  const passed = evalNode(config.root, ctx, criteria);
  return { passed, configured: true, criteria };
}

// ── Selectores de progreso (para el alumno / el reporte) ───────────────────────

export interface CompletionChip {
  key: string;
  shortLabel: string;
  met: boolean;
  /** Texto ya formateado del progreso (p.ej. "80%", "3/4"). */
  progress: string;
}

/** Chip por criterio para la línea de progreso del alumno. */
export function completionChips(status: CompletionStatus): CompletionChip[] {
  return status.criteria.map((c) => {
    const meta = completionCriterionMeta(c.key);
    let progress: string;
    switch (c.key) {
      case "attendance_pct":
      case "min_final_grade": {
        progress = `${c.current}%`;
        break;
      }
      case "challenges_passed": {
        progress = `${c.current}/${c.target}`;
        break;
      }
      default: {
        progress = c.met ? "✓" : "—";
      }
    }
    return {
      key: c.key,
      shortLabel: meta?.shortLabel ?? c.key,
      met: c.met,
      progress,
    };
  });
}

/** Línea canónica es-VE ("Asistencia 80% ✓ · 3/4 desafíos · Proyecto final ✗"). */
export function completionProgressLine(status: CompletionStatus): string {
  return completionChips(status)
    .map((c) => `${c.shortLabel} ${c.progress} ${c.met ? "✓" : "✗"}`)
    .join(" · ");
}
